import React, { useState, useEffect, useCallback } from 'react'
import { Box, Typography, IconButton } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

const PHASES = {
  INHALE: { name: 'Breathe In', duration: 4000, color: '#8b5cf6' },
  HOLD: { name: 'Hold', duration: 4000, color: '#6366f1' },
  EXHALE: { name: 'Breathe Out', duration: 6000, color: '#3b82f6' },
}

const PHASE_ORDER = ['INHALE', 'HOLD', 'EXHALE']

export default function BreathingGame() {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [cycleCount, setCycleCount] = useState(0)

  const currentPhase = PHASES[PHASE_ORDER[currentPhaseIndex]]

  const resetGame = useCallback(() => {
    setIsPlaying(false)
    setCurrentPhaseIndex(0)
    setProgress(0)
    setCycleCount(0)
  }, [])

  useEffect(() => {
    if (!isPlaying) return

    const startTime = Date.now()
    const duration = currentPhase.duration

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min(elapsed / duration, 1)
      setProgress(newProgress)

      if (newProgress >= 1) {
        clearInterval(interval)
        // Move to next phase
        const nextIndex = (currentPhaseIndex + 1) % PHASE_ORDER.length
        setCurrentPhaseIndex(nextIndex)
        setProgress(0)
        
        // Count cycles when completing a full breath
        if (nextIndex === 0) {
          setCycleCount(prev => prev + 1)
        }
      }
    }, 50)

    return () => clearInterval(interval)
  }, [isPlaying, currentPhaseIndex, currentPhase.duration])

  // Calculate circle scale based on phase and progress
  const getCircleScale = () => {
    const phase = PHASE_ORDER[currentPhaseIndex]
    if (phase === 'INHALE') {
      return 1 + progress * 0.5 // Grow from 1 to 1.5
    } else if (phase === 'HOLD') {
      return 1.5 // Stay at max
    } else {
      return 1.5 - progress * 0.5 // Shrink from 1.5 to 1
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: 3,
        minHeight: '300px',
      }}
    >
      {/* Title and cycle count */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: '14px',
            color: '#6b7280',
            fontWeight: 500,
          }}
        >
          Guided Breathing
        </Typography>
        {cycleCount > 0 && (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#9ca3af',
              mt: 0.5,
            }}
          >
            {cycleCount} breath{cycleCount > 1 ? 's' : ''} completed
          </Typography>
        )}
      </Box>

      {/* Breathing circle */}
      <Box
        sx={{
          position: 'relative',
          width: '180px',
          height: '180px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer glow ring */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${currentPhase.color}20 0%, transparent 70%)`,
            transform: `scale(${getCircleScale() * 1.2})`,
            transition: 'transform 0.1s linear',
          }}
        />
        
        {/* Main breathing circle */}
        <Box
          sx={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${currentPhase.color} 0%, ${currentPhase.color}cc 100%)`,
            boxShadow: `0 8px 32px ${currentPhase.color}40`,
            transform: `scale(${getCircleScale()})`,
            transition: 'transform 0.1s linear, background 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              textAlign: 'center',
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          >
            {isPlaying ? currentPhase.name : 'Press Play'}
          </Typography>
        </Box>
      </Box>

      {/* Progress indicator */}
      {isPlaying && (
        <Box
          sx={{
            width: '200px',
            height: '4px',
            borderRadius: '2px',
            background: '#e5e7eb',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${progress * 100}%`,
              height: '100%',
              background: currentPhase.color,
              transition: 'width 0.05s linear',
            }}
          />
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <IconButton
          onClick={() => setIsPlaying(!isPlaying)}
          sx={{
            width: '48px',
            height: '48px',
            background: isPlaying 
              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: '#ffffff',
            '&:hover': {
              background: isPlaying
                ? 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
            },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        
        <IconButton
          onClick={resetGame}
          sx={{
            width: '48px',
            height: '48px',
            background: '#f3f4f6',
            color: '#6b7280',
            '&:hover': {
              background: '#e5e7eb',
            },
          }}
        >
          <RestartAltIcon />
        </IconButton>
      </Box>

      {/* Instructions */}
      <Typography
        sx={{
          fontSize: '12px',
          color: '#9ca3af',
          textAlign: 'center',
          maxWidth: '250px',
        }}
      >
        Follow the circle as it expands and contracts. Breathe in sync with the animation.
      </Typography>
    </Box>
  )
}
